import { FC, useEffect, useRef, useState } from 'react';
import { contrast, deepCopy, isNotEmpty, refactorSelection } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import cs from 'classnames';
import { post } from '@/common/request';
import { getQiniuFileUrlWithSize } from '@/common/utils/qiniu';
import { leftSection, sectionKey } from '../../ts-config';
import { getLcnSelectionIndustry } from 'src/views/lcn/pages/sitemap/api';
// import { getBrands } from './mock';

import styles from '../index.module.less';
import { Button, Checkbox, Form, message, Pagination, Spin, Typography } from 'antd';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from 'src/common/components/Form/V2FormSelect/V2FormSelect';
import IconFont from '@/common/components/IconFont';
import V2Empty from '@/common/components/Data/V2Empty';
import V2Container from 'src/common/components/Data/V2Container/index';
import V2Form from '@/common/components/Form/V2Form';

/**
 * @description 品牌：偏好品牌/避开品牌
 * @return {*}
 * @demo
 */
const BrandBox: FC<{
  title: string,
  description: string,
  /** 是否远程搜索+分页 */
  isFuzzy?: boolean,
  /** 最多选中个数，默认 -1 无限制 */
  limit?: number,
  /** 选中的 sectionKey：preferBrand/avoidBrand/caseBrand */
  active,
  /**
   * {
    'preferBrand': [0, 95947],
    'avoidBrand': [87496],
    'caseBrand': [100441354, 100441303, 87496]
  }*/
  checked,
  handleClear?,
  handleChecked?,
  options?,
  /** 筛选参数 */
  searchParams?: { industryIds?: number[], keyword?: string },
}> = ({
  title,
  description,
  isFuzzy = false,
  limit = -1,
  active,
  checked,
  handleClear,
  handleChecked,
  options,
  searchParams,
}) => {

  const [form] = Form.useForm();
  const searchWrapperRef = useRef<any>();

  const [industries, setIndustries] = useState<any[]>([]); // 选择的行业

  const checkedValue = contrast(checked, active, []);

  const [pageParams, setPageParams] = useState<any>({ page: 1, pageSize: 20, total: 0 });
  const [mainHeight, setMainHeight] = useState<number>(0);

  const [requesting, setRequesting] = useState(false);
  const [brands, setBrands] = useState<any[]>([]);

  const searchElementRender = () => <V2Form form={form}>
    <div ref={searchWrapperRef} className={styles.searchWrapper}>
      <V2FormSelect
        name='industryIds'
        options={industries}
        config={{ mode: 'multiple', showSearch: true, maxTagCount: 1, getPopupContainer: () => searchWrapperRef.current }}
        className={styles.searchIndustry}
        placeholder='行业'
        onChange={() => methods.loadData(1)}
      />
      <V2FormInput
        name='keyword'
        config={{ suffix: <IconFont iconHref='iconsearch' /> }}
        className={styles.searchInput}
        placeholder='搜索品牌'
        onChange={() => methods.loadData(1)}
      />
    </div>
  </V2Form>;

  const checkboxElementRender = (item) => <div key={item.value} className={cs(styles.checkbox, 'inline-block')} >
    <Checkbox
      value={item.value}
      disabled={!!item.disabled}
    >
      <img src={getQiniuFileUrlWithSize(item.logo, 30)} className={styles.logo} />
      <Typography.Text className={styles.brandName} ellipsis={{
        tooltip: {
          title: item.shortName || item.name,
          getPopupContainer: (triggerNode: any) => triggerNode.parentNode
        }
      }}>
        {item.shortName || item.name}
      </Typography.Text>
    </Checkbox>
  </div>;

  /* hooks */
  useEffect(() => {
    methods.getIndustries();

    if (searchParams && Object.keys(searchParams).length) {
      form.setFieldsValue({
        industryIds: contrast(searchParams, 'industryIds', []),
        keyword: contrast(searchParams, 'keyword'),
      });
    }

    setTimeout(() => {
      methods.loadData(1);
    });
  }, []);

  const methods = useMethods({

    changePage(page) {
      methods.loadData(page);
    },

    // 行业
    getIndustries() {
      getLcnSelectionIndustry({}).then(res => {
        setIndustries(refactorSelection(Array.isArray(res) ? res : []));
      });
    },

    // 获取行业选项
    loadData(page = 1) {
      setPageParams((val) => ({ ...val, page }));

      const values = form.getFieldsValue();
      const { industryIds, keyword } = values;
      const _keyword = isNotEmpty(keyword) ? String(keyword).trim() : '';

      if (isFuzzy) {
        const params = {
          page,
          size: pageParams.pageSize,
          oneIndustryIds: industryIds, // 一级行业id集合
          name: _keyword, // 品牌名称
        };

        setRequesting(true);
        // https:// yapi.lanhanba.com/project/602/interface/api/75907
        post('/map/lcn/selection/brand', params, { isMock: false, mockId: 602, mockSuffix: '/api' }).then((res) => {
          setBrands(methods.parseOptions(res.objectList));
          setPageParams((val) => ({ ...val, total: contrast(res, 'totalNum') }));
        }).finally(() => {
          setRequesting(false);
        });
      } else {
        // 可见的品牌（搜索了关键词）
        const oriOptions = methods.parseOptions(options?.[active]);
        const _brands = isNotEmpty(_keyword) ? oriOptions.filter(item => {
          return item?.shortName?.toLowerCase()?.includes(_keyword) || item?.name?.toLowerCase()?.includes(_keyword);
        }) : oriOptions;
        setBrands(_brands);
      }

    },

    /** 统一处理选项：禁用，value 取值 */
    parseOptions(options) {
      const _options = deepCopy(options);
      return Array.isArray(_options) ? _options.map(item => ({
        ...item,
        value: item.id,
        disabled: active === sectionKey.preferBrand// 偏好品牌下，禁用已选的“避开品牌”
          ? checked?.[sectionKey.avoidBrand]?.includes(item.id)
          : (active === sectionKey.avoidBrand// 避开品牌下，禁用已选的“偏好品牌”
            ? checked?.[sectionKey.preferBrand]?.includes(item.id)
            : false)
      })) : [];
    },

    // 选中/取消选中
    checkBrand(value: any[]) {
      let result = checkedValue;

      if (isFuzzy) { // 远程搜索带分页的特殊处理
        // 需要移除的 ids
        const removeBrandIds = brands.map(item => item.value).filter(item => !value.includes(item));
        result = Array.from(new Set([...checkedValue, ...value])).filter(item => !removeBrandIds.includes(item));
      } else {
        result = value;
      }

      if (limit !== -1 && result.length > limit) {
        message.warning('已超过可选数量');
        return;
      } else {
        handleChecked(result, active);
      }
    },

    // 全选
    checkAll() {
      methods.checkBrand(brands.map(item => item.value));
    },

  });

  return <V2Container
    className={styles.brandBox}
    style={{ height: '100%' }}
    emitMainHeight={(h) => setMainHeight(h)}
    extraContent={{
      top: <div className={styles.sessionTop}>
        <div className={styles.sideBoxTop}>
          <div>{title}</div>
          <div className='mb-12 mt-4 c-999 fwNormal'>{description}</div>
          <div>
            {leftSection.find(section => section.code === active)?.label}
            {/* {
              checkedValue?.length
                ? <span className={styles.clear} onClick={() => handleClear(active)}>清除</span>
                : <></>
            } */}
          </div>
          <div className='mb-12 mt-4 c-999 fwNormal'>{leftSection.find(section => section.code === active)?.remark}</div>
        </div>
        {searchElementRender()}
        <div className='mt-10'>
          {limit === -1 && <Button type='link' size='small' className={styles.textButton} onClick={methods.checkAll}>[全选]</Button>}
          <Button type='link' size='small' className={styles.textButton} onClick={() => handleClear(active)}>[清空]</Button>
        </div>
      </div>,
    }}>
    <div className={styles.brandBoxContainer} style={{ height: mainHeight }}>
      <Spin spinning={requesting}>
        {brands.length
          ? <Checkbox.Group value={checkedValue} onChange={methods.checkBrand} >
            {brands.map((item) => checkboxElementRender(item))}
          </Checkbox.Group>
          : <div style={{ marginTop: '32px' }}><V2Empty customTip='未找到相关品牌'/></div>}
        {/* 目前看来只有品牌可以搜索，所以提示语写死，未来有扩展需要修改 */}
      </Spin>
      {!!isFuzzy && <Pagination size='small' current={pageParams.page} pageSize={pageParams.pageSize} showSizeChanger={false} total={pageParams.total} className='mt-10' onChange={methods.loadData} />}
    </div>
  </V2Container>;
};
export default BrandBox;
